import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { request } from '@/api/client';
import type { MeResponse, TokenPair } from '@/types/auth';
import { useStripeKeyStore } from '@/stores/stripeKey';

const ACCESS_KEY = 'admin_access_token';
const REFRESH_KEY = 'admin_refresh_token';

/**
 * Состояние авторизации админ-панели: access/refresh токены (в localStorage) и
 * профиль текущего юзера с эффективными правами. login/refresh шлём «сырым» fetch,
 * минуя интерсептор {@link request}, чтобы не зациклить обновление токена.
 */
export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(localStorage.getItem(ACCESS_KEY));
  const refreshToken = ref<string | null>(localStorage.getItem(REFRESH_KEY));
  const user = ref<MeResponse | null>(null);

  // Единый in-flight промис обновления токена: параллельные запросы (например
  // 6 PUT из saveAll() редактора карт) при протухшем access ловят 401 разом и
  // все зовут refresh(). Refresh-токен одноразовый (сервер ротирует его), поэтому
  // без дедупликации первый вызов отзывает токен, а остальные ловят 401 → clear()
  // → разлогин. Тут все ждут один настоящий сетевой refresh.
  let refreshPromise: Promise<boolean> | null = null;

  const isAuthenticated = computed(() => !!accessToken.value);
  /** Battle Royale есть на этом контуре (сервер шлёт в /auth/me; на проде false). */
  const battleRoyale = computed(() => user.value?.battleRoyale !== false);

  function setTokens(access: string, refresh: string) {
    accessToken.value = access;
    refreshToken.value = refresh;
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  }

  function clear() {
    accessToken.value = null;
    refreshToken.value = null;
    user.value = null;
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    // Ключ Stripe живёт в памяти вкладки — с выходом из панели он тоже забывается.
    useStripeKeyStore().forget();
  }

  async function login(username: string, password: string) {
    const res = await fetch('/api/admin/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      throw new Error(await errorMessage(res, 'Login failed'));
    }
    const data: TokenPair = await res.json();
    setTokens(data.accessToken, data.refreshToken);
    user.value = data.user ?? null;
    if (!user.value) await fetchMe();
  }

  /**
   * Пытается обновить access по refresh. true если удалось.
   * Параллельные вызовы разделяют один сетевой запрос (см. {@link refreshPromise}).
   */
  function refresh(): Promise<boolean> {
    if (refreshPromise) return refreshPromise;
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
    return refreshPromise;
  }

  async function doRefresh(): Promise<boolean> {
    if (!refreshToken.value) return false;
    let res: Response;
    try {
      res = await fetch('/api/admin/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refreshToken.value }),
      });
    } catch {
      return false;
    }
    if (!res.ok) {
      clear();
      return false;
    }
    const data: TokenPair = await res.json();
    setTokens(data.accessToken, data.refreshToken);
    if (data.user) user.value = data.user;
    return true;
  }

  async function fetchMe() {
    user.value = await request<MeResponse>('/api/admin/auth/me', { showError: false });
  }

  async function logout() {
    if (refreshToken.value) {
      try {
        await fetch('/api/admin/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refreshToken.value }),
        });
      } catch {
        /* игнорируем — всё равно чистим локально */
      }
    }
    clear();
  }

  /** Есть ли право (superadmin — всегда true). */
  function can(permission: string): boolean {
    // Выключенный Battle Royale прячет вкладку от всех, включая суперадмина (сервер её API и не отдаёт).
    if (permission.startsWith('battle-royale:') && !battleRoyale.value) return false;
    if (user.value?.superadmin) return true;
    return user.value?.permissions?.includes(permission) ?? false;
  }

  return {
    accessToken,
    refreshToken,
    user,
    isAuthenticated,
    battleRoyale,
    login,
    refresh,
    fetchMe,
    logout,
    clear,
    can,
  };
});

async function errorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const text = await res.text();
    if (!text) return fallback;
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed.error === 'string') return parsed.error;
    } catch {
      return text;
    }
    return text;
  } catch {
    return fallback;
  }
}
